import { useState } from "react";
import { Box, Group, Paper, SimpleGrid, Text, UnstyledButton } from "@mantine/core";
import { Badge, Button } from "@xplatform/design-system";
import { IconChevronRight, IconEdit, IconEye, IconRobot } from "@tabler/icons-react";
import type { AgentExecutionViewModel, FlowConfigViewModel, FlowStepViewModel } from "@models/doctype";
import { useTranslation } from "@hooks/useTranslation";
import { translateStepType } from "@utils/flowTypeTranslations";
import { StepEditDialog } from "../StepEditDialog/StepEditDialog";
import { AgentDetailModal } from "../AgentDetailModal";
import { countOverrides, llmConfigSections, stepRuntimeSections } from "../RuntimeOptions";
import "./FlowStepList.scss";

interface FlowStepListProps {
    flowConfig: FlowConfigViewModel;
    docTypeId: string;
    /** The version number a save from any editor will create. */
    nextVersion: number;
    canEdit: boolean;
    onSaved: (config: FlowConfigViewModel) => void;
}

const SAMPLING_KEYS = ["temperature", "topP", "topK", "maxOutputTokens", "thinkingBudget", "seed"] as const;

/** Options a step sets beyond provider, model and prompts (sampling + runtime). */
const stepOverrides = (step: FlowStepViewModel) => {
    const llm = step.llmStepConfig;
    if (!llm) return Object.keys(step.ocrStepConfig?.ocrOptions ?? {}).length;
    const options = llm.configOptions ?? {};
    const sampling = SAMPLING_KEYS.filter((key) => options[key] != null).length;
    const runtime = llmConfigSections(step.stepType).reduce((sum, section) => sum + countOverrides(section, options), 0);
    const stepRuntime = stepRuntimeSections(step.stepType).reduce((sum, section) => sum + countOverrides(section, llm), 0)
        + Object.keys(llm.chunkingOptions?.max_pages_per_domain ?? {}).length;
    return sampling + runtime + stepRuntime;
};

const agentOverrides = (agent: AgentExecutionViewModel) => {
    const options = agent.configOptions ?? {};
    return SAMPLING_KEYS.filter((key) => options[key] != null).length
        + llmConfigSections("DATA_EXTRACTION").reduce((sum, section) => sum + countOverrides(section, options), 0)
        + (agent.fallbackModel ? 1 : 0) + (agent.systemPrompt ? 1 : 0);
};

/**
 * The flow's steps, in order, as a readable list: what each step runs on and
 * how much it customises, with its agents listed inside it. Editing opens a
 * side panel, so the list stays in view.
 */
export const FlowStepList = ({ flowConfig, docTypeId, nextVersion, canEdit, onSaved }: FlowStepListProps) => {
    const { t } = useTranslation();
    const [editingStep, setEditingStep] = useState<FlowStepViewModel | null>(null);
    const [editingAgent, setEditingAgent] = useState<{ agent: AgentExecutionViewModel; step: FlowStepViewModel } | null>(null);

    const steps = [...flowConfig.steps].sort((a, b) => a.stepOrder - b.stepOrder);

    return (
        <Box className="flow-step-list">
            {steps.map((step, index) => {
                const config = step.llmStepConfig ?? step.ocrStepConfig;
                const agents = step.llmStepConfig?.executions ?? [];
                const temperature = step.llmStepConfig?.configOptions?.temperature;
                const overrides = stepOverrides(step);
                return (
                    <Box key={step.stepOrder} className="flow-step-list__item">
                        <Box className="flow-step-list__rail" aria-hidden>
                            <span className="flow-step-list__order">{step.stepOrder}</span>
                            {index < steps.length - 1 && <span className="flow-step-list__line" />}
                        </Box>
                        <Paper withBorder radius="md" p="md" className="flow-step-list__card">
                            <Group justify="space-between" wrap="nowrap" align="flex-start">
                                <Box miw={0}>
                                    <Text fw={600}>{translateStepType(step.stepType, t)}</Text>
                                    <Group gap="xs" mt="xs">
                                        {config?.provider && <Badge variant="light" color="gray" size="sm">{config.provider}</Badge>}
                                        {config?.modelName && (
                                            <Badge variant="light" color="blue" size="sm" tt="none">{config.modelName}</Badge>
                                        )}
                                        {temperature != null && (
                                            <Badge variant="light" color="gray" size="sm" tt="none">
                                                {t("admin.flowConfigs.steps.temperature", { value: temperature })}
                                            </Badge>
                                        )}
                                        {agents.length > 0 && (
                                            <Badge variant="light" color="violet" size="sm" leftSection={<IconRobot size={12} />}>
                                                {t("admin.flowConfigs.steps.agents", { count: agents.length })}
                                            </Badge>
                                        )}
                                        {overrides > 0 && (
                                            <Badge variant="light" color="orange" size="sm">
                                                {t("admin.flowConfigs.options.overrides", { count: overrides })}
                                            </Badge>
                                        )}
                                    </Group>
                                </Box>
                                <Button
                                    variant="light"
                                    size="xs"
                                    leftSection={canEdit ? <IconEdit size={14} /> : <IconEye size={14} />}
                                    onClick={() => setEditingStep(step)}
                                    aria-label={t(canEdit ? "admin.flowConfigs.steps.editStep" : "admin.flowConfigs.steps.viewStep", {
                                        order: step.stepOrder,
                                    })}
                                >
                                    {canEdit ? t("common.edit") : t("admin.flowConfigs.steps.view")}
                                </Button>
                            </Group>

                            {agents.length > 0 && (
                                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="xs" mt="md">
                                    {agents.map((agent) => {
                                        const agentCount = agentOverrides(agent);
                                        return (
                                            <UnstyledButton
                                                key={agent.domainKey}
                                                className="flow-step-list__agent"
                                                onClick={() => setEditingAgent({ agent, step })}
                                            >
                                                <IconRobot size={16} color="var(--mantine-color-violet-6)" />
                                                <Box miw={0} style={{ flex: 1 }}>
                                                    <Text size="sm" fw={600} truncate>{agent.domainKey}</Text>
                                                    <Text size="xs" c="dimmed" truncate>
                                                        {agent.modelName || t("admin.flowConfigs.steps.stepModel")}
                                                        {agentCount > 0 && ` · ${t("admin.flowConfigs.options.overrides", { count: agentCount })}`}
                                                    </Text>
                                                </Box>
                                                <IconChevronRight size={14} color="var(--mantine-color-dimmed)" />
                                            </UnstyledButton>
                                        );
                                    })}
                                </SimpleGrid>
                            )}
                        </Paper>
                    </Box>
                );
            })}

            {editingStep && (
                <StepEditDialog
                    open
                    onClose={() => setEditingStep(null)}
                    onSaved={onSaved}
                    step={editingStep}
                    docTypeId={docTypeId}
                    flowConfig={flowConfig}
                    nextVersion={nextVersion}
                    canEdit={canEdit}
                />
            )}

            {editingAgent && (
                <AgentDetailModal
                    opened
                    onClose={() => setEditingAgent(null)}
                    agent={editingAgent.agent}
                    step={editingAgent.step}
                    flowConfig={flowConfig}
                    docTypeId={docTypeId}
                    onSaved={onSaved}
                    nextVersion={nextVersion}
                    canEdit={canEdit}
                />
            )}
        </Box>
    );
};
